import { Card } from "@/components/ui/card";
import type { LucideIcon } from "lucide-react";

interface FinancialMetricCardProps {
  title: string;
  amount: number;
  currency?: string;
  icon: LucideIcon;
  progress?: number;
  trend?: "up" | "down";
  trendValue?: string;
  color?: string;
}

export const FinancialMetricCard = ({
  title,
  amount,
  currency = "₹",
  icon: Icon,
  progress = 0,
  trend,
  trendValue,
  color = "primary",
}: FinancialMetricCardProps) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const colorClasses = {
    primary: "text-primary",
    success: "text-success",
    warning: "text-warning",
    info: "text-info",
  };

  const bgColorClasses = {
    primary: "bg-primary/10",
    success: "bg-success/10",
    warning: "bg-warning/10",
    info: "bg-info/10",
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-card-gradient-start to-card-gradient-end border-border/50">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-foreground">
            {currency}
            {amount.toLocaleString("en-IN")}
          </h3>
          {trend && trendValue && (
            <p
              className={`text-xs mt-1 ${
                trend === "up" ? "text-success" : "text-destructive"
              }`}
            >
              {trend === "up" ? "↑" : "↓"} {trendValue}
            </p>
          )}
        </div>
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90">
            <circle
              cx="48"
              cy="48"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-muted/20"
            />
            <circle
              cx="48"
              cy="48"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className={colorClasses[color as keyof typeof colorClasses]}
              style={{ transition: "stroke-dashoffset 0.5s ease" }}
            />
          </svg>
          <div
            className={`absolute inset-0 flex items-center justify-center ${
              bgColorClasses[color as keyof typeof bgColorClasses]
            } rounded-full m-3`}
          >
            <Icon
              className={colorClasses[color as keyof typeof colorClasses]}
              size={24}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};
